import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logger utility for auth errors
const logger = {
  error: (message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }
};

// Custom hook for auth methods
const useAuthMethods = (setUser, setLoading) => {
  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const loginAsGuest = useCallback(async (name = 'Guest') => {
    try {
      const response = await axios.post(`${API}/auth/guest`, { name }, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      logger.error('Guest login failed:', error);
      throw error;
    }
  }, [setUser]);

  const exchangeSession = useCallback(async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, 
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      return response.data;
    } catch (error) {
      logger.error('Session exchange failed:', error);
      throw error;
    }
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  }, [setUser]);

  return { checkAuth, loginWithGoogle, loginAsGuest, exchangeSession, logout };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const { checkAuth, loginWithGoogle, loginAsGuest, exchangeSession, logout } = useAuthMethods(setUser, setLoading);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    loading,
    loginWithGoogle,
    loginAsGuest,
    exchangeSession,
    logout,
    checkAuth,
    setUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
